import * as React from 'react';

import { faBars } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';

import logo from 'src/assets/images/logo_white.png';
import NavigationSidebarButton from 'src/features/navigation/components/NavigationSidebarButton';
import { pageTitles } from 'src/features/navigation/utils/messages';
import useScreenSize from 'src/hooks/useScreenSize';
import { useParking } from 'src/providers/ParkingProvider';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItemWithIcon,
    DropdownMenuTrigger,
} from 'src/registry/ui/DropdownMenu';
import { cn } from 'src/utils';

import type { NavigationLinkDetail } from 'src/features/navigation/utils/types';
import { PATH } from 'src/features/navigation/utils/constants';
import { ICON_MAP } from 'src/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { getLocalUserId, PERMISSION } from 'src/features/auth';
import { useListUserMemberships } from 'src/features/profile/api/useListUserMemberships';

const NavigationSidebar = () => {
    const { formatMessage } = useIntl();
    const { screenWidth } = useScreenSize();

    const [visibleNavLinksInMobile, setVisibleNavLinksInMobile] = React.useState<NavigationLinkDetail[]>([]);
    const { parkingId } = useParking();

    const userId = getLocalUserId();
    const { data: userMemberships } = useListUserMemberships({ userId });
    const isClient = Boolean(userMemberships && userMemberships[0]?.is_client);

    const accessHomeStats = useHasPermissions([PERMISSION.ACCESS_HOME_STATS]);

    const accessEntitiesPermission = useHasPermissions([PERMISSION.ACCESS_ENTITIES]);
    const accessMembershipsPermission = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIPS]);

    const accessAdministration = useHasPermissions(
        [
            PERMISSION.ACCESS_SITES,
            PERMISSION.ACCESS_SERVICE_PROVIDERS,
            PERMISSION.ACCESS_ZONES,
            PERMISSION.ACCESS_GATEWAYS,
            PERMISSION.ACCESS_SPACES,
            PERMISSION.ACCESS_ROLES,
            PERMISSION.ACCESS_USERS,
            PERMISSION.MANAGE_SYSTEM_SETTINGS,
            PERMISSION.MANAGE_SYSTEM_COMPONENTS,
            PERMISSION.ACCESS_ACCESS_KEYS,
            PERMISSION.ACCESS_MEMBERSHIP_TYPES,
            PERMISSION.ACCESS_TROUBLESHOOTING,
            PERMISSION.ACCESS_PRICING_PLANS,
            PERMISSION.MANAGE_VALIDATIONS,
        ],
        true,
    );

    const accessValidations = useHasPermissions([PERMISSION.ALLOW_VALIDATIONS]);

    const accessMonitoring = useHasPermissions(
        [
            PERMISSION.ACCESS_PAY_STATIONS_MONITORING,
            PERMISSION.ACCESS_DEVICES_MONITORING,
            PERMISSION.ACCESS_GATEWAYS_MONITORING,
        ],
        true,
    );

    const accessPayments = useHasPermissions(
        [
            PERMISSION.ACCESS_PAYMENTS,
            PERMISSION.ACCESS_PAY_STATIONS,
            PERMISSION.ACCESS_PAY_STATION_OPERATIONS,
            PERMISSION.ACCESS_PAY_STATION_EVENTS,
        ],
        true,
    );

    const accessStatistics = useHasPermissions(
        [PERMISSION.ACCESS_PAYMENT_STATISTICS, PERMISSION.ACCESS_OCCUPANCY_STATISTICS],
        true,
    );

    const accessVisitorsModule = useHasPermissions(
        [
            PERMISSION.ACCESS_ACTIVE_STAYS,
            PERMISSION.ACCESS_PAST_STAYS,
            PERMISSION.ACCESS_VISITORS,
            PERMISSION.ACCESS_GATEWAY_PASSES,
            PERMISSION.ACCESS_EVENT_LOGS,
        ],
        true,
    );

    const navigationLinkDetails: NavigationLinkDetail[] = React.useMemo(() => {
        return [
            {
                link: PATH.HOME,
                icon: ICON_MAP.HOME,
                tooltip: pageTitles.home,
                hasPermission: accessHomeStats || isClient,
            },
            {
                link: PATH.VISITORS,
                icon: ICON_MAP.CLIENT,
                tooltip: pageTitles.visitors,
                hasPermission: accessVisitorsModule,
            },
            {
                link: PATH.MEMBERSHIPS,
                icon: ICON_MAP.SUBSCRIPTION,
                tooltip: pageTitles.memberships,
                hasPermission: accessMembershipsPermission,
            },
            {
                link: PATH.ENTITIES,
                icon: ICON_MAP.USERS,
                tooltip: pageTitles.entitiesCRM,
                hasPermission: accessEntitiesPermission,
            },
            {
                link: PATH.MONITORING,
                icon: ICON_MAP.WAVE_PULSE,
                tooltip: pageTitles.monitoring,
                hasPermission: accessMonitoring,
            },
            {
                link: PATH.PAYMENTS,
                icon: ICON_MAP.CIRCLE_EURO,
                tooltip: pageTitles.payStations,
                hasPermission: accessPayments,
            },
            {
                link: PATH.STATISTICS,
                icon: ICON_MAP.PIE_CHART,
                tooltip: pageTitles.statistics,
                hasPermission: accessStatistics,
            },
            {
                link: PATH.VALIDATIONS,
                icon: ICON_MAP.TICKET_PERFORATED,
                tooltip: pageTitles.validation,
                hasPermission: accessValidations,
            },
            {
                link: PATH.PROFILE,
                icon: ICON_MAP.MEMBER,
                tooltip: pageTitles.profile,
                hasPermission: true,
            },
            {
                link: PATH.ADMINISTRATION,
                icon: ICON_MAP.COG,
                tooltip: pageTitles.settings,
                hasPermission: accessAdministration,
            },
        ];
    }, [
        accessVisitorsModule,
        accessMembershipsPermission,
        accessEntitiesPermission,
        accessMonitoring,
        accessStatistics,
        accessPayments,
        accessHomeStats,
        accessAdministration,
        accessValidations,
        isClient,
    ]);

    const navigationLinksWithPermissions: NavigationLinkDetail[] = React.useMemo(() => {
        return navigationLinkDetails.filter((item) => item.hasPermission);
    }, [navigationLinkDetails]);

    const hamburgerMenuNavigationDetails: NavigationLinkDetail[] = React.useMemo(() => {
        return navigationLinksWithPermissions.filter(
            (item) => !visibleNavLinksInMobile.some((hamburgerItem) => item.link === hamburgerItem.link),
        );
    }, [visibleNavLinksInMobile, navigationLinksWithPermissions]);

    const hamburgerMenuItems: NavigationLinkDetail[] = hamburgerMenuNavigationDetails.filter(
        (item) => item.link !== PATH.ADMINISTRATION,
    );
    const listOfVisibleMobileNavLinks: string[] = visibleNavLinksInMobile.map((item) => item.link);

    React.useEffect(() => {
        const isMobile = screenWidth < 640;
        const isLargePhone = screenWidth > 400 && screenWidth < 640;
        const numberOfNavLinksShownOnMobile = isMobile && isLargePhone ? 4 : 3;

        const visibleNavLinks = navigationLinksWithPermissions.slice(
            0,
            isMobile ? numberOfNavLinksShownOnMobile : navigationLinksWithPermissions.length,
        );

        setVisibleNavLinksInMobile(visibleNavLinks.map((item) => item));
    }, [screenWidth, parkingId]);

    const sliceNumber = accessAdministration && !isClient ? 2 : 1;

    return (
        <aside className="bg-primary-700 fixed top-0 left-0 z-40 flex h-16 w-full flex-col overflow-x-hidden overflow-y-auto sm:h-full sm:w-20">
            <div className="mx-4 my-3 flex size-10 shrink-0 items-center justify-center rounded-md sm:m-4 sm:size-12 sm:bg-white sm:p-2 dark:sm:bg-gray-950">
                <img src={logo} alt="SmartPark" />
            </div>
            <nav className="bg-primary-700 text-primary-300 fixed bottom-0 flex w-full justify-around p-2 text-xl sm:relative sm:h-full sm:flex-col sm:justify-normal sm:gap-3 sm:px-4 sm:pt-0 sm:pb-4 dark:bg-gray-950">
                {navigationLinksWithPermissions.map((item, index) => {
                    return (
                        <React.Fragment key={item.link}>
                            {index === navigationLinksWithPermissions.length - sliceNumber && (
                                <div className="mt-auto hidden sm:flex" />
                            )}
                            <NavigationSidebarButton
                                className={cn(
                                    item.link !== 'profile' &&
                                        !listOfVisibleMobileNavLinks.includes(item.link) &&
                                        'hidden sm:flex',
                                )}
                                icon={item.icon}
                                link={item.link}
                                tooltip={formatMessage(item.tooltip)}
                            />
                        </React.Fragment>
                    );
                })}

                {hamburgerMenuItems.length > 0 && visibleNavLinksInMobile.length > 0 && (
                    <DropdownMenu modal={false}>
                        <DropdownMenuTrigger className="hover:bg-primary-600 data-[state=open]:bg-primary-600 xs:size-12 flex size-10 shrink-0 items-center justify-center rounded-md transition-colors data-[state=open]:text-white">
                            <FontAwesomeIcon icon={faBars} />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            className="bg-primary-700 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom z-20 mb-1 w-svw rounded-t-3xl rounded-b-none border-0 p-6 pb-3 text-white"
                        >
                            {hamburgerMenuItems.map((item) => {
                                {
                                    return (
                                        <NavLink to={item.link} key={item.link}>
                                            <DropdownMenuItemWithIcon
                                                variant="sidebar"
                                                iconClassName="text-primary-300"
                                                icon={item.icon}
                                            >
                                                <FormattedMessage {...item.tooltip} />
                                            </DropdownMenuItemWithIcon>
                                        </NavLink>
                                    );
                                }
                            })}
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </nav>
        </aside>
    );
};

export default NavigationSidebar;
